import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Empty, Select, Space, Table, Tag } from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import pmvService from '@/services/pmv.service';
import { hireTableCellEllipsis, hireTableLinkBtnStyle } from '@/utils/pmvHireUi';

const PmvReliabilityPredictionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<string | undefined>('ACTIVE');

  const q = useQuery({
    queryKey: ['pmv-reliability-predictions', status],
    queryFn: () =>
      pmvService.listReliabilityPredictions({
        status,
        limit: 100,
      }),
  });

  const items = q.data?.items ?? [];

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Reliability Predictions"
        subtitle="Advisory windows with method and confidence — never fabricated RUL"
        primaryActions={
          <Space wrap>
            <Button onClick={() => navigate('/asset/pmv/reliability/control-tower')}>
              Control Tower
            </Button>
            <Button onClick={() => navigate('/asset/pmv/reliability')}>Health Register</Button>
            <Button onClick={() => navigate('/asset/pmv/reliability/attention')}>Attention</Button>
            <Button onClick={() => navigate('/asset/pmv/reliability/failures')}>Failures</Button>
          </Space>
        }
        extra={
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 160 }}
            value={status}
            onChange={setStatus}
            options={['ACTIVE', 'SUPERSEDED', 'CLOSED', 'DISMISSED'].map((v) => ({
              value: v,
              label: v,
            }))}
          />
        }
      />
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Forecast based on available operational evidence. Maintenance execution remains subject to review."
      />
      {!q.isLoading && items.length === 0 ? (
        <Empty description="No predictions for these filters" />
      ) : (
        <Table
          rowKey="id"
          size="small"
          loading={q.isLoading}
          dataSource={items}
          pagination={{ pageSize: 25 }}
          scroll={{ x: 1100 }}
          tableLayout="fixed"
          columns={[
            {
              title: 'Equipment',
              dataIndex: 'equipment_label',
              width: 200,
              ellipsis: true,
              render: (v, r) => (
                <div style={hireTableCellEllipsis}>
                  <Button
                    type="link"
                    style={hireTableLinkBtnStyle}
                    onClick={() => navigate(`/asset/pmv/${r.pmv_profile_id}`)}
                  >
                    {v || 'Equipment'}
                  </Button>
                </div>
              ),
            },
            {
              title: 'Type',
              dataIndex: 'prediction_type',
              width: 160,
              ellipsis: true,
            },
            { title: 'Title', dataIndex: 'title', ellipsis: true },
            {
              title: 'Risk',
              dataIndex: 'risk_band',
              width: 100,
              render: (v) => (
                <Tag color={v === 'HIGH' || v === 'CRITICAL' ? 'red' : 'orange'}>{v || '—'}</Tag>
              ),
            },
            {
              title: 'Window',
              width: 140,
              render: (_, r) =>
                r.window_low != null && r.window_high != null
                  ? `${r.window_low}–${r.window_high} ${r.window_unit || ''}`.trim()
                  : '—',
            },
            {
              title: 'Confidence',
              dataIndex: 'confidence',
              width: 110,
              render: (v) => v || '—',
            },
            {
              title: 'Method',
              dataIndex: 'method',
              width: 140,
              ellipsis: true,
              render: (v) => v || '—',
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 120,
              render: (v) => <Tag>{v || '—'}</Tag>,
            },
          ]}
        />
      )}
    </WorkspaceLayout>
  );
};

export default PmvReliabilityPredictionsPage;
